import React, { useMemo, useState } from "react";

import { faSearch } from "@performyard/fontawesome/pro-solid-svg-icons";
import {
	ButtonStandard,
	InputText,
	Link,
	ModalConfirmation,
	Pagination,
	type SortChangedEvent,
	Table,
	Text,
} from "@performyard/py-components";
import type { CompensationCycleResponse } from "@performyard/service-contracts";

import ComponentLoader from "../../components/Spinner/ComponentLoader";

import {
	useCompensationCycleList,
	useCompensationCycleListWithMyDecisions,
	useDeleteCompensationCycle,
	useRbacPrimaryRolePermissions,
} from "../../api/hooks";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

import BasePageTemplate from "../../pageTemplates/BasePageTemplate";
import useInfoPageTemplateQueryState from "../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ErrorPage from "../Error/ErrorPage";
import { createActionsRenderer, getColumnDefs } from "./columns";

const PAGE_SIZE = 20;

export const CompensationLanding: React.FC = () => {
	const { value: compensationEnabled } = useFeatureFlagQuery("ff-compensation", undefined);

	const { data: permissions, isLoading: isCompensationPermissionsLoading } =
		useRbacPrimaryRolePermissions("compensation");
	const deleteMutation = useDeleteCompensationCycle();

	const [cycleToDelete, setCycleToDelete] = useState<CompensationCycleResponse | null>(null);

	const canCreateCycle = permissions?.includes("compensation:createCycle");
	const canViewAllCycles = permissions?.includes("compensation:viewCycles") ?? false;
	const canViewCyclesWithDecisionsOnly = permissions?.includes("compensation:viewCyclesWithDecisionsToMake") ?? false;

	const userCanViewCompensation = isCompensationPermissionsLoading
		? undefined
		: canViewAllCycles || canViewCyclesWithDecisionsOnly;

	const compensationListMode = useMemo((): "all" | "myDecisions" | null => {
		if (permissions === undefined) return null;
		if (canViewAllCycles) return "all";
		if (canViewCyclesWithDecisionsOnly) return "myDecisions";
		return null;
	}, [permissions, canViewAllCycles, canViewCyclesWithDecisionsOnly]);

	const { pageNumber, sort, setPageNumber, setSort, searchValue, debouncedSearchValue, setDebouncedSearch } =
		useInfoPageTemplateQueryState({
			defaultSortName: "dueDate",
			defaultSortDirection: "desc",
			defaultPageNumber: 1,
		});

	const sortParam = sort.name ? `${sort.direction}(${sort.name})` : undefined;

	const listQueryParams = {
		skip: (pageNumber - 1) * PAGE_SIZE,
		limit: PAGE_SIZE,
		sort: sortParam,
		search: debouncedSearchValue || undefined,
	};
	const allCyclesQuery = useCompensationCycleList(listQueryParams, {
		enabled: compensationListMode === "all",
	});
	const myDecisionsCyclesQuery = useCompensationCycleListWithMyDecisions(listQueryParams, {
		enabled: compensationListMode === "myDecisions",
	});

	const data = compensationListMode === "all" ? allCyclesQuery.data : myDecisionsCyclesQuery.data;
	const isLoading = compensationListMode === "all" ? allCyclesQuery.isLoading : myDecisionsCyclesQuery.isLoading;

	const cycles = data?.results ?? [];
	const total = data?.total ?? 0;
	const pageCount = Math.ceil(total / PAGE_SIZE);

	const onSortChanged = (event: SortChangedEvent<CompensationCycleResponse>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const handleDelete = (cycle: CompensationCycleResponse) => {
		setCycleToDelete(cycle);
	};

	const confirmDelete = () => {
		if (cycleToDelete) {
			deleteMutation.mutate(cycleToDelete._id, {
				onSettled: () => setCycleToDelete(null),
			});
		}
	};

	const ActionsRenderer = useMemo(() => createActionsRenderer(!!canCreateCycle, handleDelete), [canCreateCycle]);

	const columnDefs = useMemo(
		() => getColumnDefs(!!canCreateCycle, ActionsRenderer),
		[canCreateCycle, ActionsRenderer]
	);

	if (isCompensationPermissionsLoading || compensationEnabled === undefined) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}
	if (compensationEnabled === false) {
		return <ErrorPage code={403} />;
	}

	if (userCanViewCompensation === false) {
		return <ErrorPage code={403} />;
	}

	return (
		<BasePageTemplate title="Compensation Management">
			<div className="flex flex-col pt-6 [&>*+*]:mt-8">
				<div className="">
					<Text size="heading-5" weight="semi-bold" overrideElement="h1">
						Compensation Management
					</Text>

					<Text size="paragraph-2">
						Run merit-based compensation cycles with access to employee performance reviews and goals{" "}
					</Text>
				</div>

				<div className="flex flex-col gap-4">
					<div className="flex items-center justify-between gap-4">
						<div className="w-80">
							<InputText
								value={searchValue}
								onChange={(value) => setDebouncedSearch(value)}
								placeholder="Search cycles..."
								leftIcon={faSearch}
								data-testid="compensation-search-input"
							/>
						</div>
						{canCreateCycle && (
							<Link href="/compensation/create/">
								<ButtonStandard color="primary" data-testid="create-cycle-button">
									Create New Cycle
								</ButtonStandard>
							</Link>
						)}
					</div>

					<Table<CompensationCycleResponse>
						autoHeight
						minHeight={356}
						rowData={cycles}
						columnDefs={columnDefs}
						loading={isLoading}
						getRowId={(params) => params.data._id}
						onSortChanged={onSortChanged}
						emptyStateText="No Compensation Cycles Found"
						initialState={{
							sort: {
								sortModel: sort.name
									? [{ colId: sort.name, sort: sort.direction as "asc" | "desc" }]
									: [],
							},
						}}
						animateRows={false}
					/>

					{pageCount > 1 && (
						<div className="flex w-full justify-center">
							<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
						</div>
					)}

					{cycleToDelete && (
						<ModalConfirmation
							isOpen={!!cycleToDelete}
							onClose={() => setCycleToDelete(null)}
							dangerouslySetClassName="max-w-[432px]"
							title="Delete Compensation Cycle"
							message={`Are you sure you want to delete "${cycleToDelete.name}"? This action cannot be undone.`}
							negativeButtonProps={{
								children: "Cancel",
								onClick: () => setCycleToDelete(null),
							}}
							positiveButtonProps={{
								children: "Delete",
								onClick: confirmDelete,
								color: "red",
								"data-testid": "confirm-delete-button",
							}}
						/>
					)}
				</div>
			</div>
		</BasePageTemplate>
	);
};
